// CloudinaryPicker — modal that lets you pick from Cloudinary library OR upload a new image.
// Used in: Landing Editor (cover & gallery), and any future "pick image" flow.
import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, Upload, Search, ImageIcon as ImageIc, Check, Loader2 } from "lucide-react";

export default function CloudinaryPicker({
  open,
  onClose,
  onPick,             // (asset: {secure_url, public_id}) => void
  multi = false,      // if true, allows selecting multiple before confirming
  onPickMany,         // (assets: [{secure_url, public_id}]) => void — used when multi=true
  folderHint = "",    // narrows the library list
}) {
  const [tab, setTab] = useState("upload");   // library | upload — default to upload (faster)
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(folderHint);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState([]);   // for multi mode
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSelected([]);
      setSearch(folderHint);
      setTab("upload");   // start on Upload — most common action
    }
    // eslint-disable-next-line
  }, [open]);

  // Lazy-load library only when user switches to that tab
  useEffect(() => {
    if (open && tab === "library") load();
    // eslint-disable-next-line
  }, [tab, open]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("resource_type", "image");
      params.set("max_results", "60");
      if (folderHint) params.set("folder", folderHint);
      const r = await api.get(`/api/cloudinary/list?${params.toString()}`, { _silent401: true });
      const items = r.data?.items || r.data?.resources || (Array.isArray(r.data) ? r.data : []);
      setList(Array.isArray(items) ? items : []);
    } catch (e) {
      toast.error("Could not load library");
    } finally { setLoading(false); }
  };

  const filtered = (Array.isArray(list) ? list : []).filter(it => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return `${it.public_id || ""} ${it.folder || ""} ${it.filename || ""}`.toLowerCase().includes(q);
  });

  const handlePick = (it) => {
    const asset = { secure_url: it.secure_url || it.url, public_id: it.public_id };
    if (multi) {
      setSelected(prev => prev.some(s => s.public_id === asset.public_id)
        ? prev.filter(s => s.public_id !== asset.public_id)
        : [...prev, asset]);
    } else {
      onPick && onPick(asset);
      onClose();
    }
  };

  const confirmMulti = () => {
    if (selected.length === 0) return toast.error("Pick at least one");
    onPickMany && onPickMany(selected);
    onClose();
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    await uploadFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const uploaded = [];
    for (const f of files) {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name} not an image`); continue; }
      if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name} > 15 MB`); continue; }
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("folder", folderHint || "landing/uploads");
        fd.append("resource_type", "image");
        const r = await api.post("/api/cloudinary/upload", fd);
        uploaded.push({ secure_url: r.data.secure_url, public_id: r.data.public_id });
      } catch (err) {
        toast.error(`Upload failed: ${f.name}`);
      }
    }
    setUploading(false);
    if (uploaded.length === 0) return;
    if (multi) {
      onPickMany && onPickMany(uploaded);
      onClose();
    } else {
      onPick && onPick(uploaded[0]);
      onClose();
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragOver) setDragOver(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only clear when leaving the modal entirely (relatedTarget not inside)
    if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
  };
  const onDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []).filter(f => f.type.startsWith("image/"));
    if (!files.length) {
      toast.error("Drop image files only");
      return;
    }
    // Auto-switch to upload tab if dropped while on library
    if (tab !== "upload") setTab("upload");
    await uploadFiles(files);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="cloudinary-picker"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}
      onClick={onClose}>
      <div className="bg-zinc-950 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        style={{ border: `1px solid ${dragOver ? "var(--gio-primary)" : "var(--gio-border)"}` }}
        data-testid="cloudinary-picker-modal">
        {/* Drag overlay */}
        {dragOver && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-2xl pointer-events-none"
            style={{ background: "rgba(20,184,166,0.18)", border: "2px dashed var(--gio-primary)" }}
            data-testid="picker-drop-overlay">
            <Upload size={48} style={{ color: "var(--gio-primary)" }} />
            <div className="text-[18px] font-heading mt-3" style={{ color: "var(--gio-primary)" }}>Drop images to upload</div>
            <div className="text-[12px] mt-1" style={{ color: "var(--gio-text)" }}>
              {multi ? "Multiple files supported" : "First image will be picked"}
            </div>
          </div>
        )}
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: "var(--gio-border)" }}>
          <h3 className="font-heading text-lg" style={{ color: "var(--gio-text)" }}>Pick image</h3>
          <button onClick={onClose} data-testid="cloudinary-picker-close" className="hover:text-red-500">
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b" style={{ borderColor: "var(--gio-border)" }}>
          <TabBtn label="📚 Library" active={tab === "library"} onClick={() => setTab("library")} testid="picker-tab-library" />
          <TabBtn label="📤 Upload new" active={tab === "upload"} onClick={() => setTab("upload")} testid="picker-tab-upload" />
        </div>

        {tab === "library" ? (
          <>
            <div className="p-3 border-b" style={{ borderColor: "var(--gio-border)" }}>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--gio-muted)" }} />
                <input value={search} onChange={(e) => setSearch(e.target.value)}
                  data-testid="picker-search" placeholder="Filter by name/folder…"
                  className="gio-input w-full pl-9" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {loading && <div className="text-center py-12 text-[13px]" style={{ color: "var(--gio-muted-3)" }}>Loading…</div>}
              {!loading && filtered.length === 0 && (
                <div className="text-center py-12 text-[13px]" style={{ color: "var(--gio-muted-3)" }}>
                  No images. Switch to "Upload new" or change folder.
                </div>
              )}
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {filtered.map((it, i) => {
                  const isSelected = selected.some(s => s.public_id === it.public_id);
                  return (
                    <button key={it.public_id || i} onClick={() => handlePick(it)}
                      data-testid={`picker-item-${i}`}
                      className="relative aspect-square rounded-lg overflow-hidden border-2 transition hover:scale-105"
                      style={{ borderColor: isSelected ? "var(--gio-primary)" : "var(--gio-border)" }}>
                      <img src={it.secure_url || it.url} alt={it.public_id}
                        className="w-full h-full object-cover" loading="lazy" />
                      {multi && isSelected && (
                        <div className="absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center"
                          style={{ background: "var(--gio-primary)", color: "#000" }}>
                          <Check size={14} strokeWidth={3} />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            {multi && (
              <div className="p-3 border-t flex items-center justify-between" style={{ borderColor: "var(--gio-border)" }}>
                <span className="text-[12px]" style={{ color: "var(--gio-muted-2)" }}>{selected.length} selected</span>
                <button onClick={confirmMulti} data-testid="picker-confirm-multi" className="btn-primary text-[12px]">
                  Add {selected.length || ""}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 p-8 flex items-center justify-center">
            <label className="block border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer hover:bg-zinc-900 transition w-full"
              style={{ borderColor: dragOver ? "var(--gio-primary)" : "var(--gio-border)" }} data-testid="picker-upload-zone">
              {uploading ? (
                <Loader2 size={36} className="mx-auto animate-spin" style={{ color: "var(--gio-primary)" }} />
              ) : (
                <Upload size={36} className="mx-auto mb-3" style={{ color: dragOver ? "var(--gio-primary)" : "var(--gio-muted)" }} />
              )}
              <div className="text-[14px] font-bold mt-3" style={{ color: "var(--gio-text)" }}>
                {uploading ? "Uploading…" : `Drop files here or click to upload ${multi ? "(multi-select)" : ""}`}
              </div>
              <div className="text-[11px] mt-1" style={{ color: "var(--gio-muted-3)" }}>
                JPG / PNG / WEBP up to 15 MB each · drag & drop supported
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" multiple={multi} hidden onChange={onUpload}
                data-testid="picker-upload-input" />
            </label>
          </div>
        )}
      </div>
    </div>
  );
}

function TabBtn({ label, active, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="px-5 py-3 text-[12px] font-bold uppercase tracking-wider border-b-2 transition"
      style={{
        color: active ? "var(--gio-primary)" : "var(--gio-muted-2)",
        borderColor: active ? "var(--gio-primary)" : "transparent",
      }}>{label}</button>
  );
}
